import { injectable } from 'inversify';
import { decodeJwt, SignJWT } from 'jose';
import { JWT_SECRET } from 'src/config';
import { JwtServiceInterface } from 'src/features/auth/data/service/jwtService.types';

@injectable()
class JwtService implements JwtServiceInterface {
  private readonly secret = new TextEncoder().encode(JWT_SECRET);

  public async getOrCreateToken(
    payload: Record<string, unknown>,
    storageKey: string
  ): Promise<string> {
    let token = localStorage.getItem(storageKey);

    if (token) {
      try {
        const decodedPayload = decodeJwt(token);

        const isPayloadMatching = Object.entries(payload).every(
          ([key, value]) => decodedPayload[key] === value
        );

        if (isPayloadMatching) {
          return token;
        }
      } catch (error) {
        console.warn('Error decoding JWT, regenerating token.', error);
      }
    }

    token = await new SignJWT(payload)
      .setProtectedHeader({ alg: 'HS256' })
      .sign(this.secret);

    localStorage.setItem(storageKey, token);
    return token;
  }

  public decodeToken<T>(token: string): T {
    try {
      const decoded = decodeJwt(token);
      if (!decoded || typeof decoded !== 'object') {
        throw new Error('Invalid token format');
      }
      return decoded as T;
    } catch (error) {
      throw new Error(
        `Failed to decode JWT: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }
}

export default JwtService;
